import { LoadingPanel } from "@/components/LoadingPanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import {
  COMPLIANCE_CAPABILITIES,
  COMPLIANCE_CAPABILITY_LABEL,
  type Contractor,
  HMF_TRADES,
  getSupabase,
} from "@/lib/supabase";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, Pencil, Plus, RefreshCcw, Save, Trash2, UserMinus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const schema = z
  .object({
    name: z.string().trim().min(1).max(200),
    email: z.string().trim().email().optional().or(z.literal("")),
    phone: z.string().trim().max(40).optional().or(z.literal("")),
    trades: z.array(z.string()).min(1, "Pick at least one trade"),
    compliance_capabilities: z.array(z.string()).default([]),
    notes: z.string().trim().max(500).optional().or(z.literal("")),
    is_active: z.boolean(),
  })
  .refine((v) => !!v.email || !!v.phone, {
    message: "Email or phone required",
    path: ["email"],
  });
type FormValues = z.infer<typeof schema>;

export function AdminContractors() {
  const { user } = useAuth();
  const adminName =
    (user?.user_metadata?.full_name as string | undefined)?.trim() ||
    user?.email?.split("@")[0] ||
    "Trinium Property";
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Contractor | "new" | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const supabase = getSupabase();
    const { data, error: err } = await supabase
      .from("contractors")
      .select("*")
      .order("name", { ascending: true });
    if (err) setError(err.message);
    setContractors((data as Contractor[] | null) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) return <LoadingPanel />;

  return (
    <div className="flex flex-col gap-6">
      {error ? (
        <p role="alert" className="text-sm text-red-300">
          {error}
        </p>
      ) : null}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold tracking-tight">Contractors</h2>
        <Button size="sm" onClick={() => setEditing(editing === "new" ? null : "new")}>
          <Plus size={14} />
          {editing === "new" ? "Cancel" : "Add contractor"}
        </Button>
      </div>

      {editing ? (
        <ContractorForm
          initial={editing === "new" ? null : editing}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
          onCancel={() => setEditing(null)}
        />
      ) : null}

      {contractors.length === 0 ? (
        <p className="text-sm text-ink-dim">
          No contractors yet. Add one so the dispatch agent has someone to email.
        </p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {contractors.map((c) => (
            <li key={c.id}>
              <ContractorCard
                contractor={c}
                adminName={adminName}
                onEdit={() => setEditing(c)}
                onChanged={() => void refresh()}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ContractorCard({
  contractor,
  adminName,
  onEdit,
  onChanged,
}: {
  contractor: Contractor;
  adminName: string;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [inviteMsg, setInviteMsg] = useState<string | null>(null);

  async function toggleActive() {
    setBusy(true);
    await getSupabase()
      .from("contractors")
      .update({ is_active: !contractor.is_active })
      .eq("id", contractor.id);
    setBusy(false);
    onChanged();
  }

  async function remove() {
    if (!confirm(`Delete ${contractor.name}? This can't be undone.`)) return;
    setBusy(true);
    await getSupabase().from("contractors").delete().eq("id", contractor.id);
    setBusy(false);
    onChanged();
  }

  async function invite() {
    if (!contractor.email) {
      setInviteMsg("Add an email address before inviting.");
      return;
    }
    setBusy(true);
    setInviteMsg(null);
    const sb = getSupabase();
    const { data: session } = await sb.auth.getSession();
    const token = session.session?.access_token;
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-contractor`;
    try {
      const r = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ contractor_id: contractor.id, admin_name: adminName }),
      });
      const out = await r.json();
      if (!r.ok || out.error) {
        setInviteMsg(out.error ?? `Invite failed (${r.status})`);
      } else if (out.status === "invited") {
        setInviteMsg("Invite sent.");
      } else if (out.status === "linked_existing") {
        setInviteMsg("Linked existing user — they can sign in now.");
      } else if (out.status === "already_linked") {
        setInviteMsg("Already linked.");
      } else if (out.status === "reinvited") {
        setInviteMsg("Fresh sign-in link sent.");
      } else {
        setInviteMsg("Done.");
      }
    } catch (e) {
      setInviteMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  async function cancelInvite() {
    if (
      !confirm(
        `Cancel ${contractor.name}'s portal access? Their existing magic link will stop working and you'll need to invite them again.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setInviteMsg(null);
    const sb = getSupabase();
    const { data: session } = await sb.auth.getSession();
    const token = session.session?.access_token;
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-contractor-invite`;
    try {
      const r = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ contractor_id: contractor.id }),
      });
      const out = await r.json();
      if (!r.ok || out.error) {
        setInviteMsg(out.error ?? `Cancel failed (${r.status})`);
      } else if (out.status === "user_deleted") {
        setInviteMsg("Portal access cancelled.");
      } else if (out.status === "unlinked_only") {
        setInviteMsg(`Unlinked. ${out.reason ?? ""}`);
      } else {
        setInviteMsg("Done.");
      }
    } catch (e) {
      setInviteMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="truncate">{contractor.name}</CardTitle>
            <CardDescription className="truncate">
              {contractor.email ?? contractor.phone ?? "—"}
            </CardDescription>
          </div>
          <span
            className={`shrink-0 text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
              contractor.is_active
                ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
                : "bg-white/5 text-ink-dim border-line"
            }`}
          >
            {contractor.is_active ? "Active" : "Inactive"}
          </span>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-1.5">
          {contractor.trades.map((t) => (
            <span
              key={t}
              className="text-[11px] px-2 py-0.5 rounded-full bg-bg-card border border-line text-ink"
            >
              {t}
            </span>
          ))}
        </div>
        {contractor.phone && contractor.email ? (
          <p className="text-xs text-ink-dim">{contractor.phone}</p>
        ) : null}
        {contractor.notes ? (
          <p className="text-xs text-ink-dim whitespace-pre-wrap">{contractor.notes}</p>
        ) : null}
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="ghost" onClick={onEdit} disabled={busy}>
            <Pencil size={14} /> Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={toggleActive} disabled={busy}>
            {contractor.is_active ? "Deactivate" : "Activate"}
          </Button>
          {!contractor.user_id ? (
            <Button size="sm" variant="ghost" onClick={invite} disabled={busy || !contractor.email}>
              <Mail size={14} /> Invite as portal user
            </Button>
          ) : (
            <>
              <span className="text-[11px] uppercase tracking-wider text-emerald-200 self-center">
                Portal linked
              </span>
              <Button size="sm" variant="ghost" onClick={invite} disabled={busy}>
                <RefreshCcw size={14} /> Reissue link
              </Button>
              <Button size="sm" variant="ghost" onClick={cancelInvite} disabled={busy}>
                <UserMinus size={14} /> Cancel invite
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={remove} disabled={busy}>
            <Trash2 size={14} /> Delete
          </Button>
        </div>
        {inviteMsg ? <p className="text-xs text-ink-dim">{inviteMsg}</p> : null}
      </CardContent>
    </Card>
  );
}

function ContractorForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial: Contractor | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initial
      ? {
          name: initial.name,
          email: initial.email ?? "",
          phone: initial.phone ?? "",
          trades: initial.trades,
          compliance_capabilities: initial.compliance_capabilities ?? [],
          notes: initial.notes ?? "",
          is_active: initial.is_active,
        }
      : {
          trades: [],
          compliance_capabilities: [],
          is_active: true,
          name: "",
          email: "",
          phone: "",
          notes: "",
        },
  });
  const [globalError, setGlobalError] = useState<string | null>(null);
  const trades = watch("trades") ?? [];
  const capabilities = watch("compliance_capabilities") ?? [];

  function toggleTrade(t: string) {
    if (trades.includes(t)) {
      setValue(
        "trades",
        trades.filter((x) => x !== t),
        { shouldValidate: true },
      );
    } else {
      setValue("trades", [...trades, t], { shouldValidate: true });
    }
  }

  function toggleCapability(c: string) {
    if (capabilities.includes(c)) {
      setValue(
        "compliance_capabilities",
        capabilities.filter((x) => x !== c),
        { shouldValidate: true },
      );
    } else {
      setValue("compliance_capabilities", [...capabilities, c], { shouldValidate: true });
    }
  }

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError(null);
    const supabase = getSupabase();
    const payload = {
      name: values.name,
      email: values.email || null,
      phone: values.phone || null,
      trades: values.trades,
      compliance_capabilities: values.compliance_capabilities,
      notes: values.notes || null,
      is_active: values.is_active,
    };
    const { error } = initial
      ? await supabase.from("contractors").update(payload).eq("id", initial.id)
      : await supabase.from("contractors").insert(payload);
    if (error) {
      setGlobalError(error.message);
      return;
    }
    onSaved();
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{initial ? "Edit contractor" : "New contractor"}</CardTitle>
        <CardDescription>
          The dispatch agent considers active contractors and picks the best fit for each repair
          based on trades, compliance capabilities and the triage report.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid sm:grid-cols-2 gap-4" onSubmit={onSubmit} noValidate>
          <Field
            id="name"
            label="Company / contact name"
            error={errors.name?.message}
            className="sm:col-span-2"
          >
            <Input id="name" placeholder="Smith Plumbing & Heating Ltd" {...register("name")} />
          </Field>
          <Field id="email" label="Email" error={errors.email?.message}>
            <Input
              id="email"
              type="email"
              placeholder="dispatch@smithph.co.uk"
              {...register("email")}
            />
          </Field>
          <Field id="phone" label="Phone" error={errors.phone?.message}>
            <Input id="phone" placeholder="07900 123 456" {...register("phone")} />
          </Field>
          <Field
            id="trades"
            label="Trades covered"
            error={errors.trades?.message as string | undefined}
            className="sm:col-span-2"
          >
            <div className="flex flex-wrap gap-2">
              {HMF_TRADES.map((t) => {
                const on = trades.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTrade(t)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                      on
                        ? "bg-accent text-accent-on border-accent"
                        : "bg-bg-card text-ink-dim border-line hover:text-ink"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field
            id="compliance_capabilities"
            label="Compliance capabilities (optional)"
            error={errors.compliance_capabilities?.message as string | undefined}
            className="sm:col-span-2"
          >
            <div className="flex flex-wrap gap-2">
              {COMPLIANCE_CAPABILITIES.map((c) => {
                const on = capabilities.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCapability(c)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                      on
                        ? "bg-accent text-accent-on border-accent"
                        : "bg-bg-card text-ink-dim border-line hover:text-ink"
                    }`}
                  >
                    {COMPLIANCE_CAPABILITY_LABEL[c]}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-ink-dim mt-1.5">
              Used by the smart-pick agent to match contractors to compliance jobs (gas safety,
              boiler service, EICR, EPC).
            </p>
          </Field>
          <Field id="notes" label="Notes" error={errors.notes?.message} className="sm:col-span-2">
            <Input
              id="notes"
              placeholder="Out of hours number, working hours, etc."
              {...register("notes")}
            />
          </Field>
          <Field id="is_active" label="Active" className="sm:col-span-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" {...register("is_active")} />
              <span>Available for dispatch</span>
            </label>
          </Field>
          {globalError ? (
            <p role="alert" className="text-xs text-red-300 sm:col-span-2">
              {globalError}
            </p>
          ) : null}
          <div className="sm:col-span-2 flex gap-2">
            <Button type="submit" disabled={isSubmitting}>
              <Save size={14} />
              {isSubmitting ? "Saving…" : "Save"}
            </Button>
            <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
